import { useId } from 'react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { LibraryItemState } from '@/lib/api/generated/model';
import {
  stateBadges,
  type StateBadgeSpec,
  type StateFact,
  type Tone,
} from '../labels';

// Text from the contrast-verified `--state-*` tier, a tint of the matching
// `--fill-*` behind it. The transition is the page's one motion: a badge that
// changes state after an install fades to its new colour instead of snapping.
//
// The neutral tone is outlined rather than filled: in the light theme
// `--secondary`, `--muted` and `--background` are all the same surface, so a
// filled neutral badge was bold text on the page with no pill around it, while
// the dark theme drew a pill. The border is what makes it the same object in
// both — the dependency chips beside it are built the same way.
const TONE_CLASS: Record<Tone, string> = {
  neutral: 'bg-muted text-muted-foreground border-border',
  success: 'bg-fill-success/20 text-state-success',
  info: 'bg-fill-info/40 text-state-info',
  warning: 'bg-fill-warning/25 text-state-warning',
  destructive: 'bg-destructive/10 text-destructive',
};

/**
 * Where this workspace stands against an item, in the spec's words (FR-003).
 *
 * Badges rather than a sentence, because they are read across a whole list at
 * once — and one per fact rather than one per state, because a state carries as
 * many as three: the item is here, it has been changed here, and the library
 * has moved on. The tone is exposed as data so a test can assert it without
 * knowing the class names, and so the class names can change without the tests.
 */
export function StateBadge({
  state,
  installable,
  reason,
  reasonId: existingReasonId,
  omit,
  also,
}: {
  state: LibraryItemState;
  installable: boolean;
  /** Rendered for assistive technology when nothing visible already says it. */
  reason?: string | null;
  /** The id of an element that already shows the reason, to point at instead. */
  reasonId?: string;
  /** Facts this surface does not draw — the list drops "not installed". */
  omit?: readonly StateFact[];
  /**
   * Said after the state, for something true of the item that its own state
   * cannot carry: a workflow whose parts have gone missing is still installed.
   */
  also?: readonly StateBadgeSpec[];
}) {
  const ownReasonId = useId();
  const badges = [
    ...stateBadges(state, installable).filter(
      (badge) => !omit?.includes(badge.fact),
    ),
    ...(also ?? []),
  ];
  const describe = !installable
    ? (existingReasonId ?? (reason ? ownReasonId : undefined))
    : undefined;

  if (badges.length === 0) return null;

  return (
    <span className="flex flex-wrap items-center justify-end gap-1">
      {badges.map((badge) => (
        <Badge
          key={badge.fact}
          variant="secondary"
          data-tone={badge.tone}
          aria-describedby={
            badge.fact === 'not_installable' ? describe : undefined
          }
          className={cn(
            'transition-colors motion-safe:duration-[var(--motion-duration-slow)]',
            TONE_CLASS[badge.tone],
          )}
        >
          {badge.label}
        </Badge>
      ))}
      {describe === ownReasonId && (
        <span id={ownReasonId} className="sr-only">
          {reason}
        </span>
      )}
    </span>
  );
}
